import type { components } from '@connected/api-client/api.generated';
import type { Prettify } from '@connected/api-client/lib/utils';
import type { FormApi, ReactFormApi } from '@tanstack/react-form';
import type { ZodValidator } from '@tanstack/zod-form-adapter';

import { api } from '@connected/api-client';
import { validators } from '@connected/api-client/generated/validators.generated';
import { CheckboxField } from '@connected/form-fields/components/checkbox';
import FieldInfo from '@connected/form-fields/components/field-info';
import { GradientField } from '@connected/form-fields/components/gradient';
import { Label } from '@connected/form-fields/components/label';
import { NumberField } from '@connected/form-fields/components/number';
import { SingleHorizontalOptionItem, SingleOptionField } from '@connected/form-fields/components/option';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@connected/ui/components/accordion';
import { Button } from '@connected/ui/components/button';
import { Input } from '@connected/ui/components/input';
import { Skeleton } from '@connected/ui/components/skeleton';
import { Textarea } from '@connected/ui/components/textarea';
import { Container } from '@connected/ui/layouts/container';
import { booleanDefaultValue } from '@connected/ui/lib/utils';
import { useForm } from '@tanstack/react-form';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { ClipboardCopy, ClipboardPaste } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { queryClient } from '@/lib/query-client';
import { showConfirm } from '@/stores/confirmer';

function documentTemplates({ organisationId }: { organisationId: string }) {
    return api.queryOptions('get', '/api/v2/documents/templates/list', {
        params: {
            query: {
                TemplateKind: 'Electrical',
                OrganizationId: organisationId,
                PageNumber: 1,
                PageSize: 100,
                Select: {
                    Enabled: true,
                    IsDefault: true,
                    ContentKind: true,
                    TemplateKind: true,
                    GroupName: true,
                    IntervalKind: true,
                    Name: true,
                    Description: true,
                },
                Sort: ['Order'],
            },
        },
    });
}

function documentTemplateOptions({ templateId }: { templateId: string }) {
    return api.queryOptions('get', '/api/v2/documents/templates/edit', {
        params: {
            query: {
                DocumentTemplateId: templateId,
            },
        },
    });
}

function templatesCreateGet({ organisationId }: { organisationId: string }) {
    return api.queryOptions('get', '/api/v2/documents/templates/create', {
        params: {
            query: {
                OrganizationId: organisationId,
                TemplateKind: 'Electrical',
            },
        },
    });
}

export const Route = createFileRoute(
    '/(auth)/admin/(organisations)/organisation/templates/$templateId/',
)({
    component: RouteComponent,
    validateSearch: z.object({
        organisationId: z.string().min(1),
    }),
    loaderDeps: ({ search }) => ({
        organisationId: search.organisationId,
    }),
    loader: ({ context, deps, params }) => {
        context.queryClient.ensureQueryData(documentTemplateOptions(params));
        context.queryClient.ensureQueryData(templatesCreateGet(deps));
    },
});

function RouteComponent() {
    return (
        <>
            <TemplateInfo />
            <TemplateSettings />
        </>
    );
}

type TemplateEditFormValues = {
    name: string;
    description: string;
    groupName: string;
    isDefault: boolean;
    isPublic: boolean;
    enabled: boolean;
};

function TemplateInfo() {
    const { t } = useTranslation('admin');
    const search = Route.useSearch();
    const params = Route.useParams();
    const navigate = Route.useNavigate();

    // const [disabled, setDisabled] = useState(false);

    const query = useQuery(documentTemplateOptions(params));

    const editTemplateMutation = api.useMutation(
        'put',
        '/api/v2/documents/templates/edit',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(
                    documentTemplateOptions(params),
                );
            },
        },
    );

    const deleteTemplateMutation = api.useMutation(
        'delete',
        '/api/v2/documents/templates/delete',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(
                    documentTemplateOptions(params),
                );

                queryClient.invalidateQueries(
                    documentTemplates({ organisationId: search.organisationId }),
                );

                navigate({
                    to: '/admin/organisation/templates',
                    search,
                });
            },
        },
    );

    const disabled = useMemo(() =>
        query.isPending || editTemplateMutation.isPending || deleteTemplateMutation.isPending, [query.isPending, editTemplateMutation.isPending, deleteTemplateMutation.isPending]);

    const errors = useMemo(() => {
        return [editTemplateMutation.error?.Message, deleteTemplateMutation.error?.Message, query.error?.Message].filter(Boolean) as string[];
    }, [query.error, editTemplateMutation.error, deleteTemplateMutation.error]);

    const form = useForm<TemplateEditFormValues, ZodValidator>({
        defaultValues: {
            name: query.data?.DocumentTemplate?.Name || '',
            description: query.data?.DocumentTemplate?.Description || '',
            groupName: query.data?.DocumentTemplate?.GroupName || '',
            isDefault: booleanDefaultValue(query.data?.DocumentTemplate?.IsDefault) || false,
            isPublic: booleanDefaultValue(query.data?.DocumentTemplate?.IsPublic) || false,
            enabled: booleanDefaultValue(query.data?.DocumentTemplate?.Enabled) || false,
        },
        validatorAdapter: zodValidator(),
        onSubmit: async ({ value }) => {
            await editTemplateMutation.mutateAsync({
                body: {
                    DocumentTemplateId: query.data!.DocumentTemplate!.DocumentTemplateId!,
                    Name: value.name,
                    Order: query.data!.DocumentTemplate!.Order!,
                    Description: value.description,
                    GroupName: value.groupName,
                    IsDefault: value.isDefault,
                    IsPublic: value.isPublic,
                    Enabled: value.enabled,
                },
            });
            form.reset();
        },
    });

    useEffect(() => {
        form.reset();
    }, [params.templateId, form]);

    if (query.data == null)
        return <p>Template not found</p>;

    return (
        <Container className="flex flex-col gap-4">
            <Container.Header>
                <Container.Title>
                    Template algemeen
                    {' '}
                    {query.data?.DocumentTemplate?.IntervalKind === 'None' ? null : query.data.DocumentTemplate?.IntervalKind === 'First' ? '(EBI)' : query.data.DocumentTemplate?.IntervalKind === 'Repeat' ? '(PI)' : null}
                </Container.Title>
            </Container.Header>
            <Container.Content>
                <div className="flex flex-col gap-4">
                    {query.isPending
                        ? (
                                <Skeleton className="h-10" />

                            )
                        : (
                                <form.Field
                                    name="name"
                                    validators={{
                                        onChange:
                                    validators.createDocumentTemplateCommand.name,
                                    }}
                                >
                                    {({ handleChange, state, name }) => (
                                        <Label label={t('organisation.templates.form.name.label')}>
                                            <Input
                                                type="text"
                                                name={name}
                                                onChange={e =>
                                                    handleChange(e.target.value)}
                                                value={state.value}
                                                placeholder={t('organisation.templates.form.name.placeholder')}
                                                disabled={disabled}
                                            />
                                            <FieldInfo state={state} />
                                        </Label>
                                    )}
                                </form.Field>
                            )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                    {query.isPending
                        ? (
                                <Skeleton className="h-10" />
                            )
                        : (
                                <form.Field
                                    name="description"
                                    validators={{
                                        onChange:
                                    validators.createDocumentTemplateCommand.description,
                                    }}
                                >
                                    {({ handleChange, state, name }) => (
                                        <Label label={t('organisation.templates.form.description.label')}>
                                            <Input
                                                type="text"
                                                name={name}
                                                onChange={e =>
                                                    handleChange(e.target.value)}
                                                value={state.value}
                                                placeholder={t('organisation.templates.form.description.placeholder')}
                                                disabled={disabled}
                                            />
                                            <FieldInfo state={state} />
                                        </Label>
                                    )}
                                </form.Field>
                            )}
                    {query.isPending
                        ? (
                                <Skeleton className="h-10" />
                            )
                        : (
                                <form.Field
                                    name="groupName"
                                    validators={{
                                        onChange:
                                    validators.createDocumentTemplateCommand.groupName,
                                    }}
                                >
                                    {({ handleChange, state, name }) => (
                                        <Label label={t('organisation.templates.form.group.label')}>
                                            <Input
                                                type="text"
                                                name={name}
                                                onChange={e =>
                                                    handleChange(e.target.value)}
                                                value={state.value}
                                                placeholder={t('organisation.templates.form.group.placeholder')}
                                                disabled={disabled}
                                            />
                                            <FieldInfo state={state} />
                                        </Label>
                                    )}
                                </form.Field>
                            )}

                </div>
                <div className="flex justify-between gap-4">
                    {query.isPending
                        ? (
                                <Skeleton className="h-8 w-full" />
                            )
                        : (
                                <form.Field
                                    name="isDefault"
                                    validators={{
                                        onChange:
                                    validators.editDocumentTemplateCommand.isDefault,
                                    }}
                                >
                                    {({ handleChange, state, name }) => (
                                        <CheckboxField
                                            label={t('organisation.templates.form.isDefault')}
                                            name={name}
                                            value={state.value}
                                            onChange={handleChange}
                                            disabled={disabled}
                                        />
                                    )}
                                </form.Field>
                            )}
                    {query.isPending
                        ? (
                                <Skeleton className="h-8 w-full" />
                            )
                        : (
                                <form.Field
                                    name="isPublic"
                                    validators={{
                                        onChange:
                                    validators.editDocumentTemplateCommand.enabled,
                                    }}
                                >
                                    {({ handleChange, state, name }) => (
                                        <CheckboxField
                                            label={t('organisation.templates.form.isPublic')}
                                            name={name}
                                            value={state.value}
                                            onChange={handleChange}
                                            disabled={disabled}
                                        />
                                    )}
                                </form.Field>
                            )}
                    {query.isPending
                        ? (
                                <Skeleton className="h-8 w-full" />
                            )
                        : (
                                <form.Field
                                    name="enabled"
                                    validators={{
                                        onChange:
                                    validators.editDocumentTemplateCommand.enabled,
                                    }}
                                >
                                    {({ handleChange, state, name }) => (
                                        <CheckboxField
                                            label={t('organisation.templates.form.enabled')}
                                            name={name}
                                            value={state.value}
                                            onChange={handleChange}
                                            disabled={disabled}
                                        />
                                    )}
                                </form.Field>
                            )}

                </div>
                <div className="flex w-full gap-4">
                    <Button className="w-full" variant="default" onClick={form.handleSubmit} disabled={disabled}>
                        {t('organisation.templates.buttons.save')}
                    </Button>
                    <Button
                        className="w-full"
                        variant="destructive"
                        onClick={async () => {
                            if (await showConfirm(t('organisation.templates.confirmDelete'))) {
                                await deleteTemplateMutation.mutateAsync({
                                    params: {
                                        query: {
                                            DocumentTemplateId: params.templateId,
                                        },
                                    },
                                });
                            }
                        }}
                        disabled={disabled}
                    >
                        {t('organisation.templates.buttons.delete')}
                    </Button>
                </div>
                <div className="flex flex-col gap-4">
                    {errors.length > 0 && (
                        <div className="text-red-500">
                            {errors.map(error => (
                                <div key={error}>{error}</div>
                            ))}
                        </div>
                    )}
                </div>
            </Container.Content>
        </Container>
    );
}

type Tailwind = {
    [K in keyof components['schemas']['EditDocumentTemplateQueryResultTailwind']]:
        | { fieldKind: 'Text'; value: string | null }
        | { fieldKind: 'WholeNumber'; value: number | null }
        | { fieldKind: 'DecimalNumber'; value: number | null }
        | { fieldKind: 'Color'; value: string | null };
};

type Setting = Prettify<{
    documentTemplateSettingId: string;
    settingKind: string;
    enabled: boolean;
} & (
    | { fieldKind: 'ImageQuality'; value: components['schemas']['ImageQuality'] | null }
    | { fieldKind: 'Toggle'; value: boolean | null }
    | { fieldKind: 'WholeNumber'; value: number | null }
    | { fieldKind: 'DecimalNumber'; value: number | null }
    | { fieldKind: 'Date'; value: Date | null }
    | { fieldKind: 'DateTime'; value: Date | null }
    | { fieldKind: 'DateTimeOffset'; value: Date | null }
    | { fieldKind: 'Text'; value: string | null }
    | { fieldKind: 'Html'; value: string | null }
    | { fieldKind: 'Css'; value: string | null }
    )>;

type TemplateSettingsFormValues = {
    name: string;
    description: string;
    groupName: string;
    isDefault: boolean;
    isPublic: boolean;
    enabled: boolean;
    tailwind: Tailwind;
    settings: Setting[];
};

function TemplateSettings() {
    const { t } = useTranslation('admin');
    const params = Route.useParams();

    const query = useQuery(documentTemplateOptions(params));
    const editTemplateMutation = api.useMutation(
        'put',
        '/api/v2/documents/templates/edit',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(
                    documentTemplateOptions(params),
                );
            },
        },
    );

    const disabled = useMemo(() =>
        query.isPending || editTemplateMutation.isPending, [query.isPending, editTemplateMutation.isPending]);

    const errors = useMemo(() => {
        return [editTemplateMutation.error?.Message, query.error?.Message].filter(Boolean) as string[];
    }, [query.error, editTemplateMutation.error]);

    const tailwind = useMemo(() => {
        return query.data?.DocumentTemplate?.Tailwind
            ? Object.fromEntries(
                Object.entries(query.data.DocumentTemplate.Tailwind).map(([key, value]) => {
                    const colorFields = [
                        'GradientPrimary',
                        'GradientSecondary',
                        'BackgroundPrimary',
                        'BackgroundSecondary',
                        'BackgroundTertiary',
                        'BackgroundQuaternary',
                        'BackgroundPopover',
                        'BackgroundTab',
                        'TextPrimary',
                        'TextSecondary',
                        'TextTertiary',
                        'TextQuaternary',
                        'IconPrimary',
                        'IconSecondary',
                        'BrandPrimary',
                        'BrandSecondary',
                    ];

                    const fieldKind = (() => {
                        if (colorFields.includes(key)) {
                            return 'Color';
                        }
                        if (key.includes('FontWeight')) {
                            return 'WholeNumber';
                        }
                        if (key.includes('LineHeight')) {
                            return 'DecimalNumber';
                        }

                        return 'Text';
                    })();

                    return [key, { fieldKind, value }];
                }),
            ) as Tailwind
            : {};
    }, [query.data?.DocumentTemplate?.Tailwind]);

    const settings = useMemo(() => {
        return query.data?.DocumentTemplate?.Settings?.map((setting): Setting => {
            const base = {
                documentTemplateSettingId: setting.DocumentTemplateSettingId!,
                settingKind: setting.SettingKind || '',
                fieldKind: setting.FieldKind,
                enabled: setting.Enabled || false,
            };

            switch (setting.FieldKind) {
                case 'ImageQuality':
                    return {
                        ...base,
                        fieldKind: 'ImageQuality',
                        value: setting.ImageQuality ?? null,
                    };
                case 'Toggle':
                    return {
                        ...base,
                        fieldKind: 'Toggle',
                        value: setting.Toggle ?? null,
                    };
                case 'WholeNumber':
                    return {
                        ...base,
                        fieldKind: 'WholeNumber',
                        value: setting.WholeNumber ?? null,
                    };
                case 'DecimalNumber':
                    return {
                        ...base,
                        fieldKind: 'DecimalNumber',
                        value: setting.DecimalNumber ?? null,
                    };
                case 'Date':
                    return {
                        ...base,
                        fieldKind: 'Date',
                        value: setting.Date ? new Date(setting.Date) : null,
                    };
                case 'DateTime':
                    return {
                        ...base,
                        fieldKind: 'DateTime',
                        value: setting.DateTime ? new Date(setting.DateTime) : null,
                    };
                case 'DateTimeOffset':
                    return {
                        ...base,
                        fieldKind: 'DateTimeOffset',
                        value: setting.DateTimeOffset ? new Date(setting.DateTimeOffset) : null,
                    };
                case 'Text':
                    return {
                        ...base,
                        fieldKind: 'Text',
                        value: setting.Text ?? null,
                    };
                case 'Html':
                    return {
                        ...base,
                        fieldKind: 'Html',
                        value: setting.Text ?? null, // no Html property so using Text
                    };
                case 'Css':
                    return {
                        ...base,
                        fieldKind: 'Css',
                        value: setting.Text ?? null, // no CSS property so using Text
                    };
                default:
                    throw new Error(`Unknown setting kind: ${setting.FieldKind}`);
            }
        }) || [];
    }, [query.data?.DocumentTemplate?.Settings]);

    const form = useForm<TemplateSettingsFormValues, ZodValidator>({
        defaultValues: {
            name: query.data?.DocumentTemplate?.Name || '',
            description: query.data?.DocumentTemplate?.Description || '',
            groupName: query.data?.DocumentTemplate?.GroupName || '',
            isDefault: booleanDefaultValue(query.data?.DocumentTemplate?.IsDefault) || false,
            isPublic: booleanDefaultValue(query.data?.DocumentTemplate?.IsPublic) || false,
            enabled: booleanDefaultValue(query.data?.DocumentTemplate?.Enabled) || false,
            tailwind,
            settings,
        },
        validatorAdapter: zodValidator(),
        onSubmit: async ({ value }) => {
            await editTemplateMutation.mutateAsync({
                body: {
                    DocumentTemplateId: query.data!.DocumentTemplate!.DocumentTemplateId!,
                    Name: value.name,
                    Order: query.data!.DocumentTemplate!.Order!,
                    Description: value.description,
                    GroupName: value.groupName,
                    IsDefault: value.isDefault,
                    IsPublic: value.isPublic,
                    Enabled: value.enabled,
                    Tailwind: Object.fromEntries(Object.entries(value.tailwind).map(([key, value]) => [key, value?.value ?? null])),
                    Settings: value.settings?.map((setting) => {
                        const base = {
                            DocumentTemplateSettingId: setting.documentTemplateSettingId,
                            Enabled: setting.enabled,
                        };
                        switch (setting.fieldKind) {
                            case 'ImageQuality':
                                return {
                                    ...base,
                                    FieldKind: 'ImageQuality',
                                    ImageQuality: setting.value,
                                };
                            case 'Toggle':
                                return {
                                    ...base,
                                    FieldKind: 'Toggle',
                                    Toggle: setting.value,
                                };
                            case 'WholeNumber':
                                return {
                                    ...base,
                                    FieldKind: 'WholeNumber',
                                    WholeNumber: setting.value,
                                };
                            case 'DecimalNumber':
                                return {
                                    ...base,
                                    FieldKind: 'DecimalNumber',
                                    DecimalNumber: setting.value,
                                };
                            case 'Date':
                                return {
                                    ...base,
                                    FieldKind: 'Date',
                                    Date: setting.value ? setting.value.toISOString() : null,
                                };
                            case 'DateTime':
                                return {
                                    ...base,
                                    FieldKind: 'DateTime',
                                    DateTime: setting.value ? setting.value.toISOString() : null,
                                };
                            case 'DateTimeOffset':
                                return {
                                    ...base,
                                    FieldKind: 'DateTimeOffset',
                                    DateTimeOffset: setting.value ? setting.value.toISOString() : null,
                                };
                            case 'Text':
                                return {
                                    ...base,
                                    FieldKind: 'Text',
                                    Text: setting.value,
                                };
                            case 'Html':
                                return {
                                    ...base,
                                    FieldKind: 'Html',
                                    Text: setting.value,
                                };
                            default:
                                throw new Error(`Unknown setting kind: unknown`);
                        }
                    }),
                },
            });
            form.reset();
        },
    });

    useEffect(() => {
        form.reset();
    }, [params.templateId, form]);

    return (
        <Container className="flex flex-col gap-4">
            <Container.Header>
                <Container.Title>
                    {t('organisation.templates.pdfSettings.title')}
                </Container.Title>
            </Container.Header>
            <Container.Content>
                <div className="flex flex-col gap-4">
                    <Accordion type="single" collapsible>
                        <form.Field name="settings">
                            {(props) => {
                                const nonImageQualitySettings = props.form.state.values.settings.filter(s => s.fieldKind !== 'ImageQuality');
                                const gradientTailwindSettings = Object.entries(props.form.state.values.tailwind).filter(([key, _]) => key.includes('Gradient'));

                                return (nonImageQualitySettings.length || gradientTailwindSettings.length) && (
                                    <AccordionItem value="General">
                                        <AccordionTrigger>{t('organisation.templates.pdfSettings.generalSettings')}</AccordionTrigger>
                                        <AccordionContent className="flex flex-col gap-4 mx-1">
                                            {props.form.state.values.settings.map((setting, index) => (
                                                setting.fieldKind !== 'ImageQuality' && (
                                                    <SettingItemField
                                                        key={`${setting.settingKind}_${setting.fieldKind}`}
                                                        setting={setting}
                                                        index={index}
                                                        form={form}
                                                        disabled={disabled}
                                                    />
                                                )
                                            ))}
                                            {Object.entries(props.form.state.values.tailwind).map(([key, twSetting]) => (
                                                key.includes('Gradient') && (
                                                    <TailwindItemField
                                                        key={`${key}_${twSetting.fieldKind}`}
                                                        twSetting={twSetting}
                                                        twKey={key as keyof Tailwind}
                                                        form={form}
                                                        disabled={disabled}
                                                    />
                                                )
                                            ))}
                                        </AccordionContent>
                                    </AccordionItem>
                                );
                            }}
                        </form.Field>
                        <form.Field name="tailwind">
                            {props => Object.entries(props.form.state.values.tailwind).filter(([key, _]) => !key.includes('Gradient')).length && (
                                <AccordionItem value="Styling">
                                    <AccordionTrigger>{t('organisation.templates.pdfSettings.stylingSettings')}</AccordionTrigger>
                                    <AccordionContent className="flex flex-col gap-4 mx-1">
                                        {Object.entries(props.form.state.values.tailwind).map(([key, twSetting]) => (
                                            !key.includes('Gradient') && (
                                                <TailwindItemField
                                                    key={`${key}_${twSetting.fieldKind}`}
                                                    twSetting={twSetting}
                                                    twKey={key as keyof Tailwind}
                                                    form={form}
                                                    disabled={disabled}
                                                />
                                            )
                                        ))}
                                    </AccordionContent>
                                </AccordionItem>
                            )}
                        </form.Field>
                        <form.Field name="settings">
                            {props => props.form.state.values.settings.filter(s => s.fieldKind === 'ImageQuality').length && (
                                <AccordionItem value="ImageQuality">
                                    <AccordionTrigger>{t('organisation.templates.pdfSettings.imageQualitySettings')}</AccordionTrigger>
                                    <AccordionContent className="flex flex-col gap-4 mx-1">

                                        {props.form.state.values.settings.map((setting, index) => (
                                            setting.fieldKind === 'ImageQuality' && (
                                                <SettingItemField
                                                    key={`${setting.settingKind}_${setting.fieldKind}`}
                                                    setting={setting}
                                                    index={index}
                                                    form={form}
                                                    disabled={disabled}
                                                />
                                            )
                                        ))}
                                    </AccordionContent>
                                </AccordionItem>
                            )}
                        </form.Field>
                    </Accordion>
                    <Button className="w-full" variant="default" onClick={form.handleSubmit} disabled={disabled}>
                        {t('organisation.templates.actions.save')}
                    </Button>
                    <div className="flex flex-col gap-4">
                        {errors.length > 0 && (
                            <div className="text-red-500">
                                {errors.map(error => (
                                    <div key={error}>{error}</div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </Container.Content>
        </Container>
    );
}

export type TailwindItemFieldProps = {
    twSetting: Tailwind[keyof Tailwind];
    twKey: keyof Tailwind;
    form: FormApi<TemplateSettingsFormValues, ZodValidator> & ReactFormApi<TemplateSettingsFormValues, ZodValidator>;
    disabled?: boolean;
};

export function TailwindItemField({ twSetting, twKey, form, disabled }: TailwindItemFieldProps) {
    const { t } = useTranslation('admin');

    switch (twSetting?.fieldKind) {
        case 'Text':
            return (
                <form.Field
                    key={twKey}
                    name={`tailwind.${twKey}.value`}
                    validators={{
                        onChange: validators.editDocumentTemplateCommand.editDocumentTemplateCommandTailwind[twKey as keyof typeof validators.editDocumentTemplateCommand.editDocumentTemplateCommandTailwind],
                    }}
                >
                    {({ state, handleChange, name }) => (
                        <Label label={t(`pdf-templates.tailwind.${twKey}`)}>
                            <Input
                                type="text"
                                name={name}
                                onChange={e => handleChange(e.target.value)}
                                value={state.value as string ?? ''}
                                placeholder={`${t('pdf-templates.placeholder-prefix-enter')} ${t(`pdf-templates.tailwind.${twKey}`).toLowerCase()}`}
                                disabled={disabled}
                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </form.Field>
            );
        case 'WholeNumber':
        case 'DecimalNumber':
            return (
                <form.Field
                    key={twKey}
                    name={`tailwind.${twKey}.value`}
                    validators={{
                        onChange: validators.editDocumentTemplateCommand.editDocumentTemplateCommandTailwind[twKey as keyof typeof validators.editDocumentTemplateCommand.editDocumentTemplateCommandTailwind],
                    }}
                >
                    {({ state, handleChange, name }) => (
                        <Label label={t(`pdf-templates.tailwind.${twKey}`)}>
                            <NumberField
                                name={name}
                                onChange={value => handleChange(value !== null && value !== undefined ? value.toString() : '')}
                                maxPrecision={twSetting.fieldKind === 'WholeNumber' ? 0 : 2}
                                value={state.value !== undefined && state.value !== null ? Number(state.value) : null}
                                placeholder={`${t(`pdf-templates.placeholder-prefix-enter`)} ${t(`pdf-templates.tailwind.${twKey}`).toLowerCase()}`}
                                disabled={disabled}
                            />
                            <FieldInfo state={state} />
                        </Label>
                    )}
                </form.Field>
            );
        case 'Color':
            return (
                <form.Field
                    key={twKey}
                    name={`tailwind.${twKey}.value`}
                    validators={{
                        onChange: validators.editDocumentTemplateCommand.editDocumentTemplateCommandTailwind[twKey as keyof typeof validators.editDocumentTemplateCommand.editDocumentTemplateCommandTailwind],
                    }}
                >
                    {({ state, handleChange, name }) => (
                        <Label label={t(`pdf-templates.tailwind.${twKey}`)} htmlFor={name}>
                            <div className="flex flex-row gap-2">
                                <GradientField
                                    name={name}
                                    value={String(state.value ?? '')}
                                    className="mt-1"
                                    onChange={handleChange}
                                />
                                <Button
                                    variant="outline"
                                    onClick={async () => {
                                        const value = state.value as string;
                                        await navigator.clipboard.writeText(value || '');
                                        toast.success('Waarde gekopieerd naar klembord');
                                    }}
                                >
                                    <ClipboardCopy />
                                </Button>
                                <Button
                                    variant="outline"
                                    outline="primary"
                                    onClick={async () => {
                                        const text = await navigator.clipboard.readText();
                                        handleChange(text);
                                    }}
                                >
                                    <ClipboardPaste />
                                </Button>
                            </div>
                        </Label>
                    )}
                </form.Field>
            );
        default:
            return null;
    }
}

export type SettingItemFieldProps = {
    setting: Setting;
    index: number;
    form: FormApi<TemplateSettingsFormValues, ZodValidator> & ReactFormApi<TemplateSettingsFormValues, ZodValidator>;
    disabled?: boolean;
};

export function SettingItemField({ setting, index, form, disabled }: SettingItemFieldProps) {
    const { t } = useTranslation('admin');
    const enumValues = ['Original', 'OriginalWebp', 'ExtraLarge', 'Large', 'Medium', 'Small', 'ExtraSmall'];

    switch (setting.fieldKind) {
        case 'ImageQuality':
            return (
                <div className="flex flex-col gap-2">
                    <form.Field
                        key={`${setting.settingKind}_enabled`}
                        name={`settings[${index}].enabled`}
                        validators={{
                            onChange: validators.editDocumentTemplateCommand.editDocumentTemplateCommandSetting.enabled,
                        }}
                    >
                        {({ state, handleChange, name }) => (
                            <CheckboxField
                                label={t(`pdf-templates.settings.${setting.settingKind}`)}
                                name={name}
                                value={!!state.value}
                                onChange={handleChange}
                                disabled={disabled}
                            />
                        )}
                    </form.Field>

                    <form.Subscribe selector={field => [field.values.settings[index]?.enabled]}>
                        {([Enabled]) =>
                            Enabled && (
                                <form.Field
                                    name={`settings[${index}].value`}
                                    validators={{
                                        onChange: validators.editDocumentTemplateCommand.editDocumentTemplateCommandSetting.imageQuality,
                                    }}
                                >
                                    {({ state, handleChange, name }) => (
                                        <>
                                            <SingleOptionField
                                                name={name}
                                                handleChange={value => handleChange(value as string)}
                                                state={{ value: state.value as string }}
                                                options={enumValues.map(enumValue => ({
                                                    label: enumValue,
                                                    value: enumValue,
                                                }))}
                                                render={SingleHorizontalOptionItem}
                                                dropdown={true}
                                                className="flex-row"
                                                placeholder={`${t('pdf-templates.placeholder-prefix-select')} ${t(`pdf-templates.settings.${setting.settingKind}`).toLowerCase()}`}
                                                disabled={disabled}
                                            />
                                            <FieldInfo state={state} />
                                        </>
                                    )}
                                </form.Field>
                            )}
                    </form.Subscribe>
                </div>
            );
        case 'Toggle':
            return (
                <form.Field
                    name={`settings[${index}].value`}
                    validators={{
                        onChange: validators.editDocumentTemplateCommand.editDocumentTemplateCommandSetting.toggle,
                    }}
                >
                    {({ state, handleChange, name }) => (
                        <CheckboxField
                            label={t(`pdf-templates.settings.${setting.settingKind}`)}
                            name={name}
                            value={!!state.value || !!setting.enabled}
                            onChange={(value) => {
                                handleChange(value);
                                form.setFieldValue(`settings[${index}].enabled`, value); // Set Enabled based on Toggle value
                            }}
                            disabled={disabled}
                        />
                    )}
                </form.Field>
            );
        case 'WholeNumber':
        case 'DecimalNumber':
            return (
                <div className="flex flex-col gap-2">
                    <form.Field
                        key={`${setting.settingKind}_enabled`}
                        name={`settings[${index}].enabled`}
                        validators={{
                            onChange: validators.editDocumentTemplateCommand.editDocumentTemplateCommandSetting.enabled,
                        }}
                    >
                        {({ state, handleChange, name }) => (
                            <CheckboxField
                                label={t(`pdf-templates.settings.${setting.settingKind}`)}
                                name={name}
                                value={!!state.value}
                                onChange={handleChange}
                                disabled={disabled}
                            />
                        )}
                    </form.Field>

                    <form.Subscribe selector={field => [field.values.settings[index]?.enabled]}>
                        {([Enabled]) =>
                            Enabled && (
                                <form.Field
                                    name={`settings[${index}].value`}
                                    validators={{
                                        onChange: validators.editDocumentTemplateCommand.editDocumentTemplateCommandSetting.decimalNumber,
                                    }}
                                >
                                    {({ state, handleChange, name }) => (
                                        <>
                                            <NumberField
                                                name={name}
                                                onChange={handleChange}
                                                value={state.value as number ?? null}
                                                placeholder={`${t(`pdf-templates.placeholder-prefix-enter`)} ${t(`pdf-templates.settings.${setting.settingKind}`).toLowerCase()}`}
                                                disabled={disabled}
                                            />
                                            <FieldInfo state={state} />
                                        </>
                                    )}
                                </form.Field>
                            )}
                    </form.Subscribe>
                </div>
            );
        case 'Date':
        case 'DateTime':
        case 'DateTimeOffset':
            return (
                <div className="flex flex-col gap-2">
                    <form.Field
                        key={`${setting.settingKind}_enabled`}
                        name={`settings[${index}].enabled`}
                        validators={{
                            onChange: validators.editDocumentTemplateCommand.editDocumentTemplateCommandSetting.enabled,
                        }}
                    >
                        {({ state, handleChange, name }) => (
                            <CheckboxField
                                label={t(`pdf-templates.settings.${setting.settingKind}`)}
                                name={name}
                                value={!!state.value}
                                onChange={handleChange}
                                disabled={disabled}
                            />
                        )}
                    </form.Field>

                    <form.Subscribe selector={field => [field.values.settings[index]?.enabled]}>
                        {([Enabled]) =>
                            Enabled && (
                                <form.Field
                                    name={`settings[${index}].value`}
                                    validators={{
                                        onChange: validators.editDocumentTemplateCommand.editDocumentTemplateCommandSetting.dateTimeOffset,
                                    }}
                                >
                                    {({ state, handleChange, name }) => (
                                        <>
                                            <Input
                                                type="datetime-local"
                                                name={name}
                                                onChange={e => handleChange(new Date(e.target.value))}
                                                value={state.value ? (state.value as Date).toISOString().slice(0, 16) : ''}
                                                placeholder={`${t(`pdf-templates.placeholder-prefix-select`)} ${t(`pdf-templates.settings.${setting.settingKind}`).toLowerCase()}`}
                                                disabled={disabled}
                                            />
                                            <FieldInfo state={state} />
                                        </>
                                    )}
                                </form.Field>
                            )}
                    </form.Subscribe>
                </div>
            );
        case 'Html':
            return (
                <div className="flex flex-col gap-2">
                    <form.Field
                        key={`${setting.settingKind}_enabled`}
                        name={`settings[${index}].enabled`}
                        validators={{
                            onChange: validators.editDocumentTemplateCommand.editDocumentTemplateCommandSetting.enabled,
                        }}
                    >
                        {({ state, handleChange, name }) => (
                            <CheckboxField
                                label={t(`pdf-templates.settings.${setting.settingKind}`)}
                                name={name}
                                value={!!state.value}
                                onChange={handleChange}
                                disabled={disabled}
                            />
                        )}
                    </form.Field>

                    <form.Subscribe selector={field => [field.values.settings[index]?.enabled]}>
                        {([Enabled]) =>
                            Enabled && (
                                <form.Field
                                    name={`settings[${index}].value`}
                                    validators={{
                                        onChange: validators.editDocumentTemplateCommand.editDocumentTemplateCommandSetting.text,
                                    }}
                                >
                                    {({ state, handleChange, name }) => (
                                        <>
                                            <Textarea
                                                name={name}
                                                onChange={e => handleChange(e.target.value)}
                                                value={state.value as string ?? ''}
                                                placeholder={`${t(`pdf-templates.placeholder-prefix-enter`)} ${t(`pdf-templates.settings.${setting.settingKind}`).toLowerCase()}`}
                                                disabled={disabled}

                                            />
                                            <FieldInfo state={state} />
                                        </>
                                    )}
                                </form.Field>
                            )}
                    </form.Subscribe>
                </div>
            );
        case 'Text':
            return (
                <div className="flex flex-col gap-2">
                    <form.Field
                        key={`${setting.settingKind}_enabled`}
                        name={`settings[${index}].enabled`}
                        validators={{
                            onChange: validators.editDocumentTemplateCommand.editDocumentTemplateCommandSetting.enabled,
                        }}
                    >
                        {({ state, handleChange, name }) => (
                            <CheckboxField
                                label={t(`pdf-templates.settings.${setting.settingKind}`)}
                                name={name}
                                value={!!state.value}
                                onChange={handleChange}
                                disabled={disabled}
                            />
                        )}
                    </form.Field>

                    <form.Subscribe selector={field => [field.values.settings[index]?.enabled]}>
                        {([Enabled]) =>
                            Enabled && (
                                <form.Field
                                    name={`settings[${index}].value`}
                                    validators={{
                                        onChange: validators.editDocumentTemplateCommand.editDocumentTemplateCommandSetting.text,
                                    }}
                                >
                                    {({ state, handleChange, name }) => (
                                        <>
                                            <Input
                                                type="text"
                                                name={name}
                                                onChange={e => handleChange(e.target.value)}
                                                value={state.value as string ?? ''}
                                                placeholder={`${t(`pdf-templates.placeholder-prefix-enter`)} ${t(`pdf-templates.settings.${setting.settingKind}`).toLowerCase()}`}
                                                disabled={disabled}
                                            />
                                            <FieldInfo state={state} />
                                        </>
                                    )}
                                </form.Field>
                            )}
                    </form.Subscribe>
                </div>
            );
        default:
            return null;
    }
}
